import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { isPhoneValid, PHONE_MAX_LENGTH } from '@/lib/phone';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FileText, MapPin, Calendar, Home, ArrowRight, Clock, Check, X, Eye, Trash2, ExternalLink } from 'lucide-react';
import MatchesPipelineTable from '@/components/tenant/MatchesPipelineTable';

// Applicant-facing colors. `rejected` intentionally uses a neutral slate tone
// (not red) because applicants see the polite "Paused" label - the admin
// interpretation stays red in AdminPanel.
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-50 text-blue-700',
  approved: 'bg-violet-50 text-violet-700',
  in_search: 'bg-amber-50 text-amber-700',
  matched: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-slate-100 text-slate-600',
  rejected: 'bg-slate-100 text-slate-600',
  requested: 'bg-blue-50 text-blue-700', confirmed: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-500', available: 'bg-blue-50 text-blue-700',
};

const APPLICANT_STATUS_LABEL_KEY = {
  draft: 'status.app.draft',
  submitted: 'status.app.submitted',
  approved: 'status.app.approved',
  in_search: 'status.app.in_search',
  matched: 'status.app.matched',
  archived: 'status.app.archived',
  rejected: 'status.app.rejected',
};
const APPLICANT_STATUS_SUBTITLE_KEY = {
  rejected: 'status.app.rejected.subtitle',
};

export default function TenantDashboard() {
  const { t } = useLanguage();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [matches, setMatches] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [respondingProposalId, setRespondingProposalId] = useState('');
  // The proposal currently in "availability entry" mode. While a proposal is
  // open here, its card swaps the buttons for a textarea + Confirm/Cancel.
  const [availabilityProposalId, setAvailabilityProposalId] = useState('');
  const [availabilityDraft, setAvailabilityDraft] = useState('');
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitDate, setVisitDate] = useState('');
  const [visitNotes, setVisitNotes] = useState('');
  const [visitAptId, setVisitAptId] = useState('');
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const load = async () => {
      try {
        const [appRes, docsRes, matchRes, visitRes, proposalsRes] = await Promise.all([
          api.get('/applications/me'), api.get('/documents'),
          api.get('/apartments/matches'), api.get('/visits'),
          api.get('/listing-proposals/me').catch(() => ({ data: [] })),
        ]);
        setApplication(appRes.data);
        setDocuments(docsRes.data || []);
        setMatches(matchRes.data || []);
        setVisits(visitRes.data || []);
        setProposals(proposalsRes.data || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    setProfileName(user?.name || '');
    setProfileEmail(user?.email || '');
    setProfilePhone(user?.phone || '');
  }, [user]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    const validTabs = ['overview', 'documents', 'matches', 'visits', 'account'];
    if (requestedTab && validTabs.includes(requestedTab)) {
      setActiveTab(requestedTab);
    } else {
      setActiveTab('overview');
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== 'matches' || loading) return;
    api.post('/listing-proposals/me/track-matches-page').catch(() => {});
  }, [activeTab, loading]);

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', nextTab);
    }
    setSearchParams(next, { replace: true });
  };

  const handleProposalResponse = async (proposalId, reply, availability = null) => {
    setRespondingProposalId(proposalId);
    try {
      const payload = { status: reply };
      if (reply === 'interested' && typeof availability === 'string') {
        payload.availability = availability;
      }
      const res = await api.patch(`/listing-proposals/${proposalId}/respond`, payload);
      const updated = res.data;
      setProposals(prev => prev.map(p => p.id === proposalId ? { ...p, ...updated } : p));
      if (reply === 'interested') {
        toast.success("We'll be in touch shortly to confirm a visit");
        setAvailabilityProposalId('');
        setAvailabilityDraft('');
      } else {
        toast.success('Noted, thanks for letting us know');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your reply');
    } finally {
      setRespondingProposalId('');
    }
  };

  const handleRequestVisit = async (aptId) => {
    if (!visitDate) { toast.error('Please select a date'); return; }
    try {
      await api.post('/visits', { apartment_id: aptId, requested_date: visitDate, notes: visitNotes });
      toast.success('Visit requested');
      const res = await api.get('/visits');
      setVisits(res.data || []);
      setVisitDate(''); setVisitNotes(''); setVisitAptId('');
    } catch { toast.error('Failed to request visit'); }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document removed');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveProfile = async () => {
    if (!profileName.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!profileEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!profilePhone.trim()) {
      toast.error(t('auth.phoneRequired'));
      return;
    }
    if (!isPhoneValid(profilePhone)) {
      toast.error(t('auth.phoneInvalid'));
      return;
    }
    try {
      setSavingProfile(true);
      const res = await api.patch('/auth/me', {
        name: profileName.trim(),
        email: profileEmail.trim().toLowerCase(),
        phone: profilePhone.trim(),
      });
      setUser(res.data);
      setProfileName(res.data?.name || '');
      setProfileEmail(res.data?.email || '');
      setProfilePhone(res.data?.phone || '');
      toast.success('Account details updated');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update account');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeletingAccount(true);
      await api.delete('/auth/me');
      setUser(false);
      toast.success('Account deleted');
      navigate('/');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F5F3EF]" data-testid="dashboard-loading"><div className="w-6 h-6 border-2 border-[#0B3D2E] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="tenant-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl text-[#2B2B2B] tracking-tight">{t('dash.title')}</h1>
            <p className="text-sm text-[#5a5a5a] mt-1 break-all sm:break-normal">Welcome back, {user?.name || user?.email}</p>
          </div>
          {(!application || application.status === 'draft') && (
            <Button onClick={() => navigate('/application')} className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white w-full sm:w-auto" data-testid="start-app-btn">
              {application ? 'Continue Application' : t('dash.startApp')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        <div className="mb-3 flex justify-end">
          <Button
            variant="outline"
            className="text-xs border-[#0B3D2E]/30 text-[#0B3D2E] w-full sm:w-auto"
            onClick={() => handleTabChange('account')}
            data-testid="open-account-settings-btn"
          >
            {t('dash.manageAccount')}
          </Button>
        </div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <div className="overflow-x-auto pb-1">
            <TabsList className="bg-white border border-[#2B2B2B]/10 p-1 w-max min-w-full">
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white text-sm" data-testid="tab-overview">{t('dash.overview')}</TabsTrigger>
              <TabsTrigger value="documents" className="data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white text-sm" data-testid="tab-documents">{t('dash.documents')}</TabsTrigger>
              <TabsTrigger value="matches" className="data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white text-sm" data-testid="tab-matches">{t('dash.matches')}</TabsTrigger>
              <TabsTrigger value="visits" className="data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white text-sm" data-testid="tab-visits">{t('dash.visits')}</TabsTrigger>
              <TabsTrigger value="account" className="data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white text-sm" data-testid="tab-account">{t('dash.account')}</TabsTrigger>
            </TabsList>
          </div>

          {/* Overview */}
          <TabsContent value="overview">
            {application ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 shadow-sm md:col-span-2" data-testid="app-status-card">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-heading text-lg text-[#2B2B2B]">{t('dash.status')}</h3>
                    <Badge className={`${STATUS_COLORS[application.status] || 'bg-gray-100'} border-0 text-xs font-medium`} data-testid="app-status-badge">
                      {APPLICANT_STATUS_LABEL_KEY[application.status] ? t(APPLICANT_STATUS_LABEL_KEY[application.status]) : (application.status?.replace('_', ' ') || '-')}
                    </Badge>
                  </div>
                  {APPLICANT_STATUS_SUBTITLE_KEY[application.status] && (
                    <p className="text-xs text-[#5a5a5a] mb-4" data-testid="app-status-subtitle">
                      {t(APPLICANT_STATUS_SUBTITLE_KEY[application.status])}
                    </p>
                  )}
                  {!APPLICANT_STATUS_SUBTITLE_KEY[application.status] && <div className="mb-4" />}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[10px] text-[#5a5a5a] uppercase tracking-wider">Property Type</span>
                      <p className="text-[#2B2B2B]">{application.search_criteria?.property_type || '-'}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-[#5a5a5a] uppercase tracking-wider">Budget</span>
                      <p className="text-[#2B2B2B]">{application.budget?.target_rent ? `CHF ${application.budget.target_rent} - ${application.budget.max_rent}` : '-'}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-[#5a5a5a] uppercase tracking-wider">Neighborhoods</span>
                      <p className="text-[#2B2B2B]">{application.search_criteria?.neighborhoods?.join(', ') || '-'}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-[#5a5a5a] uppercase tracking-wider">Submitted</span>
                      <p className="text-[#2B2B2B]">{application.submitted_at ? new Date(application.submitted_at).toLocaleDateString() : 'Not yet'}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 shadow-sm text-center" data-testid="matches-count-card">
                    <Home className="w-6 h-6 text-[#0B3D2E] mx-auto mb-2" />
                    <p className="text-2xl font-heading text-[#2B2B2B]">{matches.length}</p>
                    <p className="text-xs text-[#5a5a5a]">{t('dash.matches')}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 shadow-sm text-center" data-testid="visits-count-card">
                    <Calendar className="w-6 h-6 text-[#0B3D2E] mx-auto mb-2" />
                    <p className="text-2xl font-heading text-[#2B2B2B]">{visits.length}</p>
                    <p className="text-xs text-[#5a5a5a]">{t('dash.visits')}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-12 text-center shadow-sm" data-testid="no-app-card">
                <Home className="w-10 h-10 text-[#0B3D2E]/30 mx-auto mb-4" />
                <p className="text-[#5a5a5a] mb-4">{t('dash.noApp')}</p>
                <Button onClick={() => navigate('/application')} className="bg-[#0B3D2E] text-white" data-testid="start-app-empty-btn">{t('dash.startApp')}</Button>
              </div>
            )}
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents">
            <div className="bg-white rounded-lg border border-[#2B2B2B]/10 shadow-sm" data-testid="documents-list">
              {documents.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-10 h-10 text-[#0B3D2E]/30 mx-auto mb-4" />
                  <p className="text-[#5a5a5a] text-sm">No documents uploaded yet.</p>
                  <Button onClick={() => navigate('/application')} variant="outline" className="mt-4 text-sm" data-testid="upload-docs-btn">Upload Documents</Button>
                </div>
              ) : (
                <div className="divide-y divide-[#2B2B2B]/5">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-start sm:items-center justify-between gap-3 p-4 hover:bg-[#F5F3EF]/50 transition-colors" data-testid={`doc-row-${doc.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#0B3D2E]" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#2B2B2B] break-all">{doc.original_filename}</p>
                          <p className="text-[10px] text-[#5a5a5a]">{doc.doc_type?.replace('_', ' ')} &middot; {new Date(doc.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteDoc(doc.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-doc-${doc.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Matches — proposals are source of truth; email is notification only */}
          <TabsContent value="matches">
            {proposals.length > 0 && (
              <div className="mb-6 space-y-3" data-testid="proposals-section">
                <div>
                  <h3 className="font-heading text-lg text-[#2B2B2B] mb-1">{t('dash.proposalsTitle')}</h3>
                  <p className="text-xs text-[#5a5a5a]">{t('dash.proposalsSubtitle')}</p>
                </div>
                <MatchesPipelineTable
                  proposals={proposals}
                  respondingProposalId={respondingProposalId}
                  onRespondStart={(id) => setRespondingProposalId(id)}
                  onRespondEnd={() => setRespondingProposalId('')}
                  onProposalUpdate={(id, updated) => {
                    setProposals((prev) => prev.map((p) => (p.id === id ? { ...p, ...updated } : p)));
                    if (updated?.visit) {
                      api.get('/visits').then((r) => setVisits(r.data || [])).catch(() => {});
                    }
                  }}
                />
              </div>
            )}
            {matches.length === 0 && proposals.length === 0 ? (
              <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-12 text-center shadow-sm" data-testid="no-matches">
                <MapPin className="w-10 h-10 text-[#0B3D2E]/30 mx-auto mb-4" />
                <p className="text-[#5a5a5a] text-sm">{t('dash.noMatches')}</p>
              </div>
            ) : null}
            {matches.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-base text-[#2B2B2B] mb-3">{t('dash.confirmedMatchesTitle')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {matches.map(apt => (
                  <div key={apt.id} className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 shadow-sm hover-lift" data-testid={`match-card-${apt.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-heading text-lg text-[#2B2B2B]">{apt.title}</h3>
                      <Badge className={`${STATUS_COLORS[apt.status] || ''} border-0 text-xs`}>{apt.status}</Badge>
                    </div>
                    <div className="space-y-1.5 text-sm text-[#5a5a5a] mb-4">
                      <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {apt.address}</p>
                      <p>CHF {apt.rent}/mo &middot; {apt.size}m&sup2; &middot; {apt.rooms} rooms</p>
                      <p>{apt.furnished ? t('dash.furnished') : t('dash.unfurnished')}</p>
                    </div>
                    {visitAptId === apt.id ? (
                      <div className="space-y-2 border-t border-[#2B2B2B]/5 pt-3">
                        <input type="date" value={visitDate} onChange={e => setVisitDate(e.target.value)} className="w-full px-3 py-2 border border-[#2B2B2B]/10 rounded-md text-sm" data-testid="visit-date-input" />
                        <textarea value={visitNotes} onChange={e => setVisitNotes(e.target.value)} placeholder="Notes (optional)" className="w-full px-3 py-2 border border-[#2B2B2B]/10 rounded-md text-sm min-h-[60px]" data-testid="visit-notes-input" />
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-[#0B3D2E] text-white text-xs" onClick={() => handleRequestVisit(apt.id)} data-testid="confirm-visit-btn">Confirm</Button>
                          <Button size="sm" variant="ghost" className="text-xs" onClick={() => setVisitAptId('')} data-testid="cancel-visit-btn">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="text-xs border-[#0B3D2E] text-[#0B3D2E]" onClick={() => setVisitAptId(apt.id)} data-testid={`request-visit-${apt.id}`}>
                        <Calendar className="w-3 h-3 mr-1" /> {t('dash.requestVisit')}
                      </Button>
                    )}
                  </div>
                ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Visits */}
          <TabsContent value="visits">
            {visits.length === 0 ? (
              <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-12 text-center shadow-sm" data-testid="no-visits">
                <Clock className="w-10 h-10 text-[#0B3D2E]/30 mx-auto mb-4" />
                <p className="text-[#5a5a5a] text-sm">{t('dash.noVisits')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {visits.map(v => (
                  <div key={v.id} className="bg-white rounded-lg border border-[#2B2B2B]/10 p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`visit-row-${v.id}`}>
                    <div>
                      <p className="text-sm font-medium text-[#2B2B2B] break-words">{v.apartment?.title || 'Apartment'}</p>
                      <p className="text-xs text-[#5a5a5a] break-words">{v.requested_date} {v.notes && `\u2014 ${v.notes}`}</p>
                    </div>
                    <Badge className={`${STATUS_COLORS[v.status] || ''} border-0 text-xs`}>{v.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Account */}
          <TabsContent value="account">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-[#2B2B2B]/10 shadow-sm p-6 space-y-4" data-testid="account-edit-card">
                <div>
                  <h3 className="font-heading text-lg text-[#2B2B2B]">{t('dash.accountDetails')}</h3>
                  <p className="text-xs text-[#5a5a5a] mt-1">{t('dash.accountHint')}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5a5a5a]">{t('auth.name')}</Label>
                  <Input
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    className="mt-1.5 h-10"
                    data-testid="account-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5a5a5a]">{t('auth.email')}</Label>
                  <Input
                    type="email"
                    value={profileEmail}
                    onChange={(e) => setProfileEmail(e.target.value)}
                    className="mt-1.5 h-10"
                    data-testid="account-email-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5a5a5a]">{t('auth.phone')}</Label>
                  <Input
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                    className="mt-1.5 h-10"
                    placeholder="+41 79 123 45 67"
                    maxLength={PHONE_MAX_LENGTH}
                    data-testid="account-phone-input"
                  />
                </div>
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white"
                  data-testid="account-save-btn"
                >
                  {savingProfile ? t('common.loading') : t('dash.saveAccount')}
                </Button>
              </div>

              <div className="bg-white rounded-lg border border-red-200 shadow-sm p-6 space-y-4" data-testid="account-delete-card">
                <div>
                  <h3 className="font-heading text-lg text-[#2B2B2B]">{t('dash.deleteAccount')}</h3>
                  <p className="text-xs text-[#5a5a5a] mt-1">{t('dash.deleteAccountHint')}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="bg-red-600 hover:bg-red-700" data-testid="account-delete-trigger">
                      {t('dash.deleteAccount')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('dash.deleteConfirmTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('dash.deleteConfirmDesc')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="account-delete-confirm"
                      >
                        {deletingAccount ? t('common.loading') : t('dash.deleteNow')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
